import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  inputFile: [],
  fileName: "",
  fileID: "",
  selectedProjectID: null,
  currentComponent: "",
  selectedAction: "",
  resetInputColumnForm: false,
  unSavedChanges: false,
  unSavedRulesChanges: false,
  columnsData: {
    inputColumns: [],
    outputColumns: [],
  },
  mapings: [],
  fileSettings: {
    headerRow: "",
    rowsToAvoid: [],
    columnsToAvoid: [],
    sortColumn: {
      sortColumnName: "",
      sortColumnType: "",
      order: "",
      toggleSortButton: false,
    },
  },
  description: "",
  outputFileSettings: {},
  mappingsId: 1,
  editStateData: {
    data: {},
    type: "",
    editMode: false,
  },
};

export const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    setAllMappings: (state, action) => {
      const {
        mappingData,
        inputColumns = [],
        outputColumns = [],
        mappingName: fileName,
        _id,
        fileSettings,
        outputFileSettings = {},
        projectID,
        description,
      } = action.payload;

      const highestId = Math.max(
        ...mappingData.map((mapping) => mapping._id),
        0,
      );

      state.selectedProjectID = projectID || null;
      state.editStateData.editMode = true;
      state.mappingsId = highestId + 1;
      state.mapings = mappingData;
      state.columnsData.inputColumns = inputColumns;
      state.columnsData.outputColumns = outputColumns;
      state.fileName = fileName;
      state.fileID = _id;
      state.fileSettings = fileSettings;
      state.description = description || "";
      state.outputFileSettings = outputFileSettings;
    },

    setProjectData: (state, action) => {
      const { selectedProjectID, fileName, fileID } = action.payload;
      state.selectedProjectID = selectedProjectID;
      state.fileName = fileName;
      state.fileID = fileID;
    },

    setEditStateData: (state, action) => {
      state.editStateData.data = action.payload.data;
      state.editStateData.type = action.payload.type;
      state.editStateData.editMode = action.payload.editMode;
    },

    updateMapping: (state, action) => {
      state.mapings = state.mapings.map((ojb) =>
        ojb._id === action.payload._id ? action.payload : ojb,
      );
    },

    deleteInputColumn: (state, action) => {
      state.columnsData.inputColumns = state.columnsData.inputColumns.filter(
        (column) =>
          column.inColChr.toUpperCase() !== action.payload.toUpperCase(),
      );
    },

    deleteOutputColumn: (state, action) => {
      state.columnsData.outputColumns = state.columnsData.outputColumns.filter(
        (column) =>
          column.outColChr.toUpperCase() !== action.payload.toUpperCase(),
      );
    },

    deleteMappings: (state, action) => {
      state.mapings = state.mapings.filter(
        (mapping) => mapping._id !== action.payload,
      );
    },

    setWorkspaceComponent: (state, action) => {
      state.currentComponent = action.payload;
    },

    setSelectedAction: (state, action) => {
      state.selectedAction = action.payload;
    },

    saveAsInputColumn: (state, action) => {
      const existingColumnIndex = state.columnsData.inputColumns.findIndex(
        (column) => column.inColChr === action.payload.inColChr,
      );
      if (existingColumnIndex !== -1) {
        state.columnsData.inputColumns[existingColumnIndex] = action.payload;
      } else {
        state.columnsData.inputColumns.push(action.payload);
      }
      state.unSavedRulesChanges = false;
    },

    saveAsOutputColumn: (state, action) => {
      const existingColumnIndex = state.columnsData.outputColumns.findIndex(
        (column) => column.outColChr === action.payload.outColChr,
      );
      if (existingColumnIndex !== -1) {
        state.columnsData.outputColumns[existingColumnIndex] = action.payload;
      } else {
        state.columnsData.outputColumns.push(action.payload);
      }
      state.unSavedRulesChanges = false;
    },

    saveAsMappings: (state, action) => {
      const existingMappingIndex = state.mapings.findIndex(
        (mapping) => mapping._id === action.payload._id,
      );

      if (existingMappingIndex !== -1) {
        state.mapings[existingMappingIndex] = action.payload; // Update existing mapping
      } else {
        state.mapings.push({
          ...action.payload,
          _id: state.mappingsId, // Assign new _id
        });
        state.mappingsId += 1;
      }

      state.unSavedRulesChanges = false;
    },

    saveDescription: (state, action) => {
      state.description = action.payload;
    },

    setunSavedRulesChanges: (state, action) => {
      state.unSavedRulesChanges = action.payload;
    },

    sortMappings: (state, action) => {
      state.mapings = action.payload;
    },
    saveFileSettingsHeader: (state, action) => {
      state.fileSettings.headerRow = action.payload;
    },
    deleteHeaderRow: (state) => {
      state.fileSettings.headerRow = "";
    },
    setSortColumn: (state, action) => {
      state.fileSettings.sortColumn = action.payload;
    },
    removeSortColumn: (state) => {
      state.fileSettings.sortColumn = "";
    },
    setRowsToAvoid: (state, action) => {
      state.fileSettings.rowsToAvoid.push(action.payload);
    },
    setColumnsToAvoid: (state, action) => {
      state.fileSettings.columnsToAvoid.push(action.payload);
    },
    removeRowToAvoid: (state, action) => {
      state.fileSettings.rowsToAvoid = state.fileSettings.rowsToAvoid.filter(
        (row) => row !== action.payload,
      );
    },
    removeColumnToAvoid: (state, action) => {
      state.fileSettings.columnsToAvoid =
        state.fileSettings.columnsToAvoid.filter(
          (row) => row !== action.payload,
        );
    },
    saveOutputFileSettings: (state, action) => {
      state.outputFileSettings = action.payload;
    },
    clearOutputFileSettings: (state) => {
      state.outputFileSettings = {};
    },
    setInputFile: (state, action) => {
      state.inputFile = action.payload;
    },
    clearInputFile: (state) => {
      state.inputFile = [];
    },
    clearInputColumnForm: (state) => {
      state.resetInputColumnForm = !state.resetInputColumnForm;
    },
  },
});

export const {
  deleteOutputColumn,
  deleteInputColumn,
  deleteMappings,
  setWorkspaceComponent,
  saveAsInputColumn,
  saveAsOutputColumn,
  saveAsMappings,
  sortMappings,
  setSelectedAction,
  saveFileSettingsHeader,
  setSortColumn,
  removeSortColumn,
  setRowsToAvoid,
  setColumnsToAvoid,
  removeRowToAvoid,
  removeColumnToAvoid,
  updateMapping,
  deleteHeaderRow,
  saveOutputFileSettings,
  clearOutputFileSettings,
  setInputFile,
  clearInputFile,
  setAllMappings,
  setEditStateData,
  setProjectData,
  clearInputColumnForm,
  setunSavedRulesChanges,
  saveDescription,
} = workspaceSlice.actions;

export const getOutputColumnsData = (state) =>
  state.workspace.columnsData.outputColumns;

export const getSelectedAction = (state) => state.workspace.selectedAction;

export const getInputColumnsData = (state) =>
  state.workspace.columnsData.inputColumns;

export const getFileDescription = (state) => state.workspace.description;
export const getRowsToAvoid = (state) =>
  state.workspace.fileSettings.rowsToAvoid;

export const getColumnsToAvoid = (state) =>
  state.workspace.fileSettings.columnsToAvoid;
export const getSortColumnSettings = (state) =>
  state.workspace.fileSettings.sortColumn;

export const getMappings = (state) => state.workspace.mapings;
export const getFileSettings = (state) => state.workspace.fileSettings;
export const getInputFile = (state) => state.workspace.inputFile;
export const getOutputFileSettings = (state) =>
  state.workspace.outputFileSettings;

export const getEditStateData = (state) => state.workspace.editStateData;
export const getChangesFlag = (state) => state.workspace.unSavedRulesChanges;

export const getEditFileData = createSelector(
  [
    (state) => state.workspace.fileName,
    (state) => state.workspace.fileID,
    (state) => state.workspace.selectedProjectID,
  ],
  (fileName, fileID, selectedProjectID) => ({
    fileName: fileName || "",
    fileID: fileID || "",
    projectID: selectedProjectID || null,
  }),
);
export default workspaceSlice.reducer;
